import { faAdd, faCalendar, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/page-layout";
import TopBarWidget from "../components/top-dashboard-widget";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import UseGraph from "../utils/useGraph";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { useDebounceValue } from "usehooks-ts";
import { IChase } from "../types/chase-type";
import { useChaseMenu } from "../store/use-chase-menu";
import GetUser from "../utils/getUser";
import formQueryUrl from "../utils/formQueryUrl";
import TableWrapper from "../components/table-wrapper";
import { chaseHeadingData } from "../components/chase-list-components/chase-list-data";
import PaginationMenu from "../components/pagination/pagination-menu";
import ChaseRenderRow from "../components/chase-list-components/chase-render-row";

export default function Dashboard() {
  const currentUser = GetUser();
  const { onToggleRangePicker } = useRangePicker();
  const { setDates, startDate, endDate } = UseGraph();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const location = window.location.pathname;
  const chaseStatus = window.location.pathname.split("/")[2] || "";
  const { onToggleChaseMenu, onChaseMenuClose } = useChaseMenu();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useDebounceValue("", 1000);
  const [chase, setChase] = useState<IChase[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const searchQuery = new URLSearchParams(window.location.search).get("query");

  // ---------------------------------------- [ FIND CHASES ] -------------------------------
  useEffect(() => {
    const getChaseList = async () => {
      setLoading(true);
      const filtersArray = [
        { search: query },
        { status: chaseStatus },
        startDate !== null && { startDate: startDate },
        endDate !== null && { endDate: endDate },
      ];
      onChaseMenuClose();
      try {
        const res = await axiosInstance.get(
          `/chase?page=${page}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setChase(res.data.data);
        setTotalCount(res.data.totalCount);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    getChaseList();
  }, [
    query,
    chaseStatus,
    page,
    pageSize,
    currentUser?.code,
    startDate,
    endDate,
  ]);

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setPage(page);
  };

  // -------------------------------- [ NAVIGATE TO USER ] --------------------------------

  const handleClick = () => {
    navigate("/createChase");
  };

  // ------------------------------- [ SEARCH BASED ON URL QUERY ] --------------------

  useEffect(() => {
    const updateQuery = () => {
      if (searchQuery && searchRef.current) {
        searchRef.current.value = searchQuery;
        setQuery(searchQuery);
        navigate(window.location.pathname);
      }
    };

    currentUser?.id && updateQuery();
  }, [searchQuery, navigate, setQuery, searchRef, currentUser?.id]);

  // --------------------------------- [ IF JUST ONE CHASE ITEM OPEN THAT UP ] -----------

  useEffect(() => {
    if (chase.length === 1) {
      const item = chase[0];
      onToggleChaseMenu({
        id: item?.id,
        chase: item,
      });
    }
  }, [chase]);

  // ------------------------------ [ ON PAGE CHANGE GO BACK TO PAGE 1 ] -----------------

  useEffect(() => {
    setPage(1);
  }, [location]);

  return (
    <PageLayout
      pageTitle="Dashboard"
      primaryAction={
        <div className="relative z-[100] flex gap-x-4">
          <DateRangePicker onChange={setDates}>
            <div
              className="relative px-5 py-1 rounded-full appearance-none text-[16px] flex items-center justify-center h-[48px] bg-surface-elevated text-light-gray border border-edge outline-none cursor-pointer hover:border-indigo-400/40 hover:shadow-glow-sm transition-all duration-300"
              onClick={onToggleRangePicker}
            >
              <FontAwesomeIcon icon={faCalendar} size="lg" />
            </div>
          </DateRangePicker>
          <button
            onClick={handleClick}
            className="flex gap-2 text-[15px] sm:text-[16px] font-dmSans font-[600] items-center px-5 sm:px-6 h-[48px] text-white rounded-full bg-gradient-to-r from-accent to-accent-bright shadow-lg shadow-indigo-500/25 hover:shadow-glow hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faAdd} />
            New Chase
          </button>
        </div>
      }
    >
      <div className="h-full">
        <TopBarWidget />
        <div className="mt-5 bg-surface rounded-2xl border border-edge p-4 sm:p-6 font-medium shadow-card animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center w-full gap-3 mb-4">
            <div>
              <h1 className="font-bold text-text-primary text-[20px] sm:text-[22px]">
                Chase list
              </h1>
              <p className="text-light-gray font-medium text-[14px] sm:text-[16px] capitalize">
                {chaseStatus ? chaseStatus.replace("-", " ") : "All statuses"}
              </p>
            </div>
            <div className="sm:ml-auto w-full sm:w-auto rounded-full flex bg-active-background text-light-gray items-center border border-edge py-2 px-4 transition-shadow duration-300 focus-within:border-indigo-400/40 focus-within:shadow-glow-sm">
              <div className="mr-3 text-light-gray">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-xs text-blue"
                />
              </div>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search..."
                className="flex-grow w-full bg-transparent text-text-primary placeholder:text-placeholder font-medium focus:outline-none disabled:opacity-50 disabled:cursor-wait"
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 custom-scrollbar">
            <TableWrapper
              headingData={chaseHeadingData}
              isLoading={loading}
              totalCount={totalCount}
            >
              {chase?.map((item, index) => {
                return (
                  <ChaseRenderRow item={item} index={index} key={item.id} />
                );
              })}
            </TableWrapper>
          </div>
          <PaginationMenu
            totalCount={totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={OnChangeCurrentPage}
          />
        </div>
      </div>
    </PageLayout>
  );
}
